// Bug report widget — a 🐞 button any tenant user can use to report a problem straight from the
// screen they're on. Ported UX (v2): click → hide the trigger FIRST → snapshot the page with
// html2canvas (so the button/modal never appear in their own screenshot) → open a composer with a
// thumbnail preview + the last console errors/warnings + basic environment info → POST to the
// platform admin Bugs queue (src/admin/bugs.js reads what this writes).
//
// The composer is LAZY — nothing in the DOM until opened — copied exactly from
// shared/version.js's releaseNotesModal (module signal, '' not null when closed, so a closed
// composer never leaks text into the i18n page-render gates). Form fields bind the SIGNAL OBJECTS
// directly (.value=${bugTitle}, never ${bugTitle.value}) so typing never re-renders the block —
// see src/tenant/document_detail.js's payOpen record-payment form for the same idiom.
import { html, signal } from './t4.js';
import { icon } from './icons.js';
import { t } from './i18n.js';
import { api } from './api.js';
import { focusModal } from './modal.js';
import { APP_VERSION } from './version.js';
// html2canvas-PRO (not stock html2canvas): the brand CSS uses color-mix(), which Chrome resolves to
// color(srgb …) in computed styles — and stock html2canvas@1.4.1 THROWS
// `Attempting to parse an unsupported color function "color"` on it, losing the screenshot entirely
// on every page that has such an element (cashbook/bank, aging cards, active tabs…). The report then
// saved with NO screenshot (owner 2026-09-10, /co/30/bank). The maintained -pro fork parses
// color()/oklch()/lab()/color-mix(); it is otherwise a drop-in (same default export + options).
import html2canvas from 'html2canvas-pro';

// ── console ring buffer — last 20 error/warn strings, shipped as meta.console ─────────────────
// initBugConsoleTap() wraps console.error/warn ONCE (idempotent — safe to call on every screen
// mount). Originals are preserved and still invoked, so devtools output is unaffected.
const CONSOLE_MAX = 20;
let consoleBuf = [];
let tapped = false;

function stringifyArg(a){
  if(typeof a === 'string') return a;
  if(a instanceof Error) return a.stack || (a.name + ': ' + a.message);
  try { return JSON.stringify(a); } catch { return String(a); }
}

export function initBugConsoleTap(){
  if(tapped) return;
  tapped = true;
  const origError = console.error.bind(console);
  const origWarn  = console.warn.bind(console);
  function record(args){
    try {
      consoleBuf.push(Array.from(args).map(stringifyArg).join(' '));
      if(consoleBuf.length > CONSOLE_MAX) consoleBuf.shift();
    } catch { /* the tap must never itself throw */ }
  }
  console.error = (...args) => { record(args); origError(...args); };
  console.warn  = (...args) => { record(args); origWarn(...args); };
}

// ── composer state — module-level, ONE report at a time (mirrors version.js's notesOpen) ──────
export const bugReportOpen = signal(false);
const capturing   = signal(false);   // true while the trigger is hidden for the html2canvas snapshot
const screenshot  = signal('');      // data:image/jpeg;base64,... or '' (no screenshot / capture failed)
const bugTitle    = signal('');
const bugDesc     = signal('');
const bugPriority = signal('normal');
const bugPage     = signal('');
const invalid     = signal({});
const saving      = signal(false);
const submitErr   = signal('');
const reportedId  = signal(null);    // set on success — drives the inline "thank you" + auto-close
let activeCompanyId = null;

function resetForm(){
  bugTitle.value = ''; bugDesc.value = ''; bugPriority.value = 'normal';   // default NORMAL on the signal, not an <option selected>
  invalid.value = {}; saving.value = false; submitErr.value = ''; reportedId.value = null;
  screenshot.value = '';
}
const clearInvalid = k => { if(invalid.value[k]) invalid.value = { ...invalid.value, [k]: false }; };

async function openReport(companyId){
  activeCompanyId = companyId;
  resetForm();
  capturing.value = true;   // hides the fab/inline button (below) — the modal is lazy, so there is
                             // no separate "modal host" to hide: capture always finishes BEFORE
                             // bugReportOpen flips true and anything modal-shaped enters the DOM.
  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));   // let ?hidden paint
  try {
    const canvas = await html2canvas(document.body, { scale: 0.5, logging: false, useCORS: true });
    screenshot.value = canvas.toDataURL('image/jpeg', 0.6);
  } catch { screenshot.value = ''; }   // graceful fallback — the report still goes, just without one
  capturing.value = false;
  bugPage.value = location.pathname;
  bugReportOpen.value = true;
  focusModal();
}

function closeReport(){ bugReportOpen.value = false; }

async function submitReport(e){
  e.preventDefault();
  if(!bugTitle.value.trim()){ invalid.value = { title: true }; return; }
  invalid.value = {}; saving.value = true; submitErr.value = '';
  const body = {
    title: bugTitle.value.trim(),
    description: bugDesc.value.trim() || undefined,
    priority: bugPriority.value || 'normal',
    url: bugPage.value,
    meta: {
      browser: navigator.userAgent,
      os: navigator.platform,
      viewport: window.innerWidth + 'x' + window.innerHeight,
      app_version: APP_VERSION,
      console: consoleBuf.slice(),
    },
  };
  if(screenshot.value){                      // omit the field entirely when there is no screenshot
    body.screenshot = screenshot.value;
    body.meta.screenshot_mime = 'image/jpeg';
  }
  try {
    const res = await api.post('/companies/' + activeCompanyId + '/bugs', body);
    reportedId.value = (res && res.id) != null ? res.id : null;
    setTimeout(() => { bugReportOpen.value = false; }, 1500);
  } catch(err){
    submitErr.value = (err.data && err.data.error) || err.message || t('bug.submit_failed', 'Could not submit — try again');
  }
  saving.value = false;
}

// ── triggers ─────────────────────────────────────────────────────────────────────────────────
// Hidden during capture AND for as long as the composer is open (its z-index sits above the modal
// overlay so it stays reachable from every screen — see the aboo.css block in the build report).
export function bugFab(companyId){
  return html`<button type="button" class="bug-fab" ?hidden=${() => capturing.value || bugReportOpen.value}
    title=${t('bug.report', 'Report a bug')} aria-label=${t('bug.report', 'Report a bug')}
    @click=${() => openReport(companyId)}>${icon('bug', {size: 20})}</button>`;
}

// Small variant for the mobile header (t-header-brand / app-user row — see the shell owner's
// wiring notes in the build report). Same composer, same trigger logic.
export function bugInlineButton(companyId){
  return html`<button type="button" class="bug-fab-inline" ?hidden=${() => capturing.value || bugReportOpen.value}
    title=${t('bug.report', 'Report a bug')} aria-label=${t('bug.report', 'Report a bug')}
    @click=${() => openReport(companyId)}>${icon('bug', {size: 20})}</button>`;
}

// ── the composer — LAZY (nothing in the DOM until opened; '' not null when closed — see
// shared/version.js releaseNotesModal, copied exactly). Signals are read at the TOP of this one
// top-level reactive slot and used as plain values inside (per CLAUDE.md's nested-slot rule); form
// fields bind the signal OBJECTS so typing never triggers a re-render of the surrounding markup.
export function bugReportModal(){
  return html`${() => {
    if(!bugReportOpen.value) return '';
    const isSaving = saving.value, err = submitErr.value;
    const shot = screenshot.value, doneId = reportedId.value;
    return html`<div class="modal-overlay bug-overlay"
        @click=${e => { if(e.target === e.currentTarget) closeReport(); }}>
      <div class="modal-card bug-modal" role="dialog" aria-label=${t('bug.report', 'Report a bug')}>
        <div class="modal-head">
          <h3>🐞 ${t('bug.report', 'Report a bug')}</h3>
          <button type="button" class="modal-x" @click=${closeReport}>✕</button>
        </div>

        ${doneId != null
          ? html`<div class="bug-success">🐞 ${t('bug.reported_prefix', 'Bug #')}${doneId} ${t('bug.reported_suffix', 'reported — thank you')}</div>`
          : html`<form @submit=${submitReport}>
          ${shot
            ? html`<img class="bug-thumb" src=${shot} alt=${t('bug.screenshot_alt', 'Screenshot preview')}>`
            : html`<div class="bug-thumb-none">${t('bug.no_screenshot', 'No screenshot captured')}</div>`}

          <label class="form-label">${t('bug.title_label', 'Title')} *</label>
          <input class=${() => 'form-control mb-2' + (invalid.value.title ? ' is-invalid' : '')}
            .value=${bugTitle} @input=${e => { bugTitle.value = e.target.value; clearInvalid('title'); }}
            placeholder=${t('bug.title_ph', 'e.g. Totals wrong on invoice list')}>

          <label class="form-label">${t('bug.desc_label', 'Description')}</label>
          <textarea class="form-control mb-2" rows="3" .value=${bugDesc}
            @input=${e => { bugDesc.value = e.target.value; }}
            placeholder=${t('bug.desc_ph', 'Steps to reproduce, expected vs actual')}></textarea>

          <label class="form-label">${t('bug.priority_label', 'Priority')}</label>
          <select class="form-control mb-2" .value=${bugPriority}
            @change=${e => { bugPriority.value = e.target.value; }}>
            <option value="">${t('bug.select_priority', '— Select priority —')}</option>
            <option value="low">${t('bug.priority_low', 'Low')}</option>
            <option value="normal">${t('bug.priority_normal', 'Normal')}</option>
            <option value="high">${t('bug.priority_high', 'High')}</option>
            <option value="urgent">${t('bug.priority_urgent', 'Urgent')}</option>
          </select>

          <label class="form-label">${t('bug.page_label', 'Page')}</label>
          <input class="form-control mb-2" .value=${bugPage} placeholder="e.g. /co/1873/invoices" readonly>

          ${err ? html`<div class="alert alert-danger py-2 mb-2">${err}</div>` : ''}
          <div class="form-actions">
            <button type="button" class="btn btn-outline" @click=${closeReport}>${t('common.cancel', 'Cancel')}</button>
            <button type="submit" class="btn btn-primary" ?disabled=${isSaving}>${isSaving ? t('common.saving', 'Saving…') : t('bug.submit', 'Report bug')}</button>
          </div>
        </form>`}
      </div>
    </div>`;
  }}`;
}
